import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    XMarkIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    CurrencyRupeeIcon,
    ChevronDownIcon,
    CalendarIcon,
    FunnelIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    UserIcon,
    CheckIcon,
    XCircleIcon,
    BanknotesIcon,
    WalletIcon,
    ArrowLeftIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { EyeIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { TbFilterPause } from "react-icons/tb";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';
import { FaRegCopy, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const Operators = ({ children }) => {
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [cardInfo, setCardInfo] = useState([]);
    const [access_data, setAccess_data] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [formData, setFormData] = useState({
        operator_id: '',
        name: '',
        category: '',
        subCategory: '',
        operatorCode: '',
        providerId: '',
        logo: '',
        isCredit: '0',
        handler: '',
        status: 'Active'
    });
    const [isAddMode, setIsAddMode] = useState(false);

    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    });

    // Separate states for form filters and API filters
    const [filters, setFilters] = useState({
        status: '',
        name: '',
        category: '',
        subCategory: '',
        page: 1,
        limit: 10
    });

    const [apiFilters, setApiFilters] = useState({
        status: '',
        name: '',
        category: '',
        subCategory: '',
        page: 1,
        limit: 10
    });

    const statusOptions = ['All', 'Active', 'Inactive'];

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username) {
            fetchCategories();
            fetchOperators();
        }
    }, [access_data?.username, apiFilters]); // Changed to depend on apiFilters instead of filters

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}get-categories.php`);
            const result = await response.json();
            if (result.status) {
                setCategories(result.data);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchOperators = async () => {
        setLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'operators',
                    status: apiFilters.status === 'All' ? '' : apiFilters.status, // Use apiFilters
                    name: apiFilters.name,
                    category: apiFilters.category,
                    subCategory: apiFilters.subCategory,
                    page: apiFilters.page,
                    limit: apiFilters.limit
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}operator.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setCardInfo(result.dashboard);

                const apiOperators = result.data.map(operator => ({
                    operator_id: operator.operator_id,
                    name: operator.biller_name,
                    category: operator.biller_category,
                    subCategory: operator.sub_category,
                    operatorCode: operator.bill_fetch_operator_code,
                    providerId: operator.bill_fetch_provider_id,
                    logo: operator.operator_logo,
                    status: operator.status === "1" ? 'Active' : 'Inactive',
                    isCredit: operator.isCredit,
                    companyBelong: operator.companyBelong,
                    handler: operator.handler
                }));

                setOperators(apiOperators);

                setPagination({
                    currentPage: result.currentPage || 1,
                    totalPages: result.totalPages || 1,
                    totalItems: result.totalItems || apiOperators.length,
                    itemsPerPage: result.limit || 10
                });
            } else {
                setOperators([]);
                toast.error(result.message || "Failed to fetch operators data.");
            }
        } catch (error) {
            console.error("Fetch operators error:", error);
            toast.error("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);

        }
    };

    const openModal = (operator = null, isAdd = false) => {
        setIsAddMode(isAdd);
        if (isAdd) {
            setFormData({
                operator_id: '',
                name: '',
                category: '',
                subCategory: '',
                operatorCode: '',
                providerId: '',
                logo: '',
                isCredit: '0',
                handler: '',
                status: 'Active'
            });
            setIsEditing(true);
        } else {
            setSelectedOperator(operator);
            setFormData({
                operator_id: operator.operator_id,
                name: operator.name,
                category: operator.category,
                subCategory: operator.subCategory,
                operatorCode: operator.operatorCode,
                providerId: operator.providerId,
                logo: operator.logo,
                isCredit: operator.isCredit,
                handler: operator.handler,
                status: operator.status
            });
            setIsEditing(false);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedOperator(null);
        setIsEditing(false);
        setIsAddMode(false);
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        if (isAddMode) {
            closeModal();
        } else {
            setIsEditing(false);
            setFormData({
                operator_id: selectedOperator.operator_id,
                name: selectedOperator.name,
                category: selectedOperator.category,
                subCategory: selectedOperator.subCategory,
                operatorCode: selectedOperator.operatorCode,
                providerId: selectedOperator.providerId,
                logo: selectedOperator.logo,
                isCredit: selectedOperator.isCredit,
                handler: selectedOperator.handler,
                status: selectedOperator.status
            });
        }
    };

    const handleSave = async () => {
        try {
            const action = isAddMode ? 'add_operator' : 'update_operator';
            const payload = {
                query: action,
                biller_name: formData.name,
                biller_category: formData.category,
                sub_category: formData.subCategory,
                bill_fetch_operator_code: formData.operatorCode,
                bill_fetch_provider_id: formData.providerId,
                operator_logo: formData.logo,
                isCredit: formData.isCredit,
                handler: formData.handler,
                status: formData.status === 'Active' ? '1' : '0'
            };

            if (!isAddMode) {
                payload.operator_id = selectedOperator.id;
            }

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload
            };

            console.log(dataToEncrypt);
            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const apiEndpoint = isAddMode ? `${API_URL}operator-add.php` : `${API_URL}operator-update.php`;
            const response = await fetch(apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                toast.success(`Operator ${isAddMode ? 'added' : 'updated'} successfully`);
                closeModal();
                fetchOperators(); // Refresh the list
            } else {
                toast.error(result.message || `Failed to ${isAddMode ? 'add' : 'update'} operator`);
            }
        } catch (error) {
            console.error(`${isAddMode ? 'Add' : 'Update'} operator error:`, error);
            toast.error("An unexpected error occurred. Please try again.");
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyFilters = () => {
        setApiFilters({
            ...filters,
            page: 1
        });
        setFilterDrawerOpen(false);
    };

    const resetFilters = () => {
        const resetFilterState = {
            status: '',
            name: '',
            category: '',
            subCategory: '',
            page: 1,
            limit: 10
        };
        setFilters(resetFilterState);
        setApiFilters(resetFilterState);
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCategorySelect = (category) => {
        setFilters(prev => ({
            ...prev,
            category,
            page: 1
        }));
        setIsCategoryOpen(false);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setFilters(prev => ({
                ...prev,
                page: newPage
            }));
            setApiFilters(prev => ({
                ...prev,
                page: newPage
            }));
        }
    };

    const handleStatusChange = async (operatorId, newStatus) => {
        try {
            // Optimistic update
            const updatedOperators = operators.map(op =>
                op.id === operatorId ? { ...op, status: newStatus ? 'Active' : 'Inactive' } : op
            );
            setOperators(updatedOperators);

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'update_status',
                    operator_id: operatorId,
                    status: newStatus ? '1' : '0'
                }
            };

            console.log(dataToEncrypt);

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}operator-status-update.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (!result.status) {
                // Revert on failure
                const revertedOperators = operators.map(op =>
                    op.id === operatorId ? { ...op, status: newStatus ? 'Inactive' : 'Active' } : op
                );
                setOperators(revertedOperators);
                throw new Error(result.message || "Status update failed");
            }

            toast.success(`Operator status updated to ${newStatus ? 'Active' : 'Inactive'}`);
        } catch (error) {
            console.error('Status update error:', error);
            toast.error(error.message);
        }
    };

    const copyText = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied successfully`);
    };

    useEffect(() => {
        if (isModalOpen || filterDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isModalOpen, filterDrawerOpen]);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 overflow-auto">
                    {/* Filter Drawer */}
                    <AnimatePresence>
                        {filterDrawerOpen && (
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '-100%' }}
                                transition={{ type: 'tween' }}
                                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
                            >
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-medium">Filter Operator</h2>
                                        <button
                                            onClick={() => setFilterDrawerOpen(false)}
                                            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                            <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Status */}
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                                            <Menu as="div" className="relative">
                                                <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                    <span>{filters.status || 'Select Status'}</span>
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                </Menu.Button>
                                                <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                    {statusOptions.map((option) => (
                                                        <Menu.Item key={option}>
                                                            {({ active }) => (
                                                                <button
                                                                    onClick={() => handleFilterChange('status', option)}
                                                                    className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                >
                                                                    {option}
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </Menu.Items>
                                            </Menu>
                                        </div>

                                        {/* Name */}
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                            <input
                                                type="text"
                                                value={filters.name}
                                                onChange={(e) => handleFilterChange('name', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                                                placeholder="Enter operator name"
                                            />
                                        </div>

                                        {/* Category */}
                                        <div className="space-y-1 relative">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                                            <Menu as="div" className="relative">
                                                <Menu.Button
                                                    className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none"
                                                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                                                >
                                                    <span>{filters.category || 'Select Category'}</span>
                                                    {isCategoryOpen ? <FaChevronUp className="h-4 w-4" /> : <FaChevronDown className="h-4 w-4" />}
                                                </Menu.Button>
                                                <Menu.Items className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                    {categories.map((category) => (
                                                        <Menu.Item key={category}>
                                                            {({ active }) => (
                                                                <button
                                                                    onClick={() => handleCategorySelect(category)}
                                                                    className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} 
                            ${filters.category === category ? 'bg-blue-50 dark:bg-blue-900' : ''}
                            block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                >
                                                                    {category}
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </Menu.Items>
                                            </Menu>
                                        </div>

                                        {/* Sub Category */}
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sub Category</label>
                                            <input
                                                type="text"
                                                value={filters.subCategory}
                                                onChange={(e) => handleFilterChange('subCategory', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                                                placeholder="Enter sub category"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6 flex space-x-3">
                                        <button
                                            onClick={resetFilters}
                                            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            onClick={applyFilters}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            Apply Filters
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Overlay when filter drawer is open */}
                    {filterDrawerOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={() => setFilterDrawerOpen(false)}
                        />
                    )}

                    <main className="flex-1 p-6 overflow-auto">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                    Back
                                </button>
                                <h2 className="text-xl font-semibold">
                                    Operators Management
                                </h2>
                                <div className='flex flex-row gap-3'>
                                    <button
                                        onClick={() => openModal(null, true)}
                                        className="px-3 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 flex items-center"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-1" />
                                        Add Operator
                                    </button>
                                    <button
                                        onClick={() => setFilterDrawerOpen(true)}
                                        className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                                    >
                                        <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                    </button>
                                </div>
                            </div>
                            {children || (
                                <>
                                    {/* Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                        {cardInfo.map((card, index) => (
                                            <div key={index} className={`${card.color} rounded-lg shadow p-6 text-white`}>
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="text-sm font-medium opacity-80">{card.title}</p>
                                                        <h3 className="text-2xl font-bold mt-1">{card.count}</h3>
                                                    </div>
                                                    <div className="p-2 rounded-full bg-white bg-opacity-20">
                                                        {card.icon}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Operators Cards */}
                                    <div className="mb-6">
                                        {loading && (
                                            <div className="flex justify-center items-center py-12">
                                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                                            </div>
                                        )}

                                        {!loading && (
                                            <>
                                                {operators.length > 0 ? (
                                                    <div className="overflow-x-auto rounded-lg shadow">
                                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                                                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs uppercase">
                                                                <tr>
                                                                    <th className="px-3 py-2 text-left font-medium">Operator ID</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Name</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Category</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Sub Category</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Operator Code</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Handler</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Status</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                                {operators.map((operator) => (
                                                                    <tr key={operator.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                                        {/* Name */}
                                                                        <td className="px-3 py-2 align-middle font-medium text-gray-900 dark:text-white">
                                                                            {operator.operator_id}
                                                                        </td>

                                                                        {/* Name */}
                                                                        <td className="px-3 py-2 align-middle font-medium text-gray-900 dark:text-white">
                                                                            <div className='flex flex-row items-center gap-2'>
                                                                                <img
                                                                                    src={operator.logo}
                                                                                    alt={operator.name}
                                                                                    className="w-8 h-8 rounded-full object-contain"
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = "none";
                                                                                        e.target.nextSibling.style.display = "flex";
                                                                                    }}
                                                                                />
                                                                                {operator.name}
                                                                            </div>
                                                                        </td>

                                                                        {/* Category */}
                                                                        <td className="px-3 py-2 align-middle text-gray-700 dark:text-gray-200">
                                                                            {operator.category}
                                                                        </td>

                                                                        {/* Sub Category */}
                                                                        <td className="px-3 py-2 align-middle text-gray-700 dark:text-gray-200">
                                                                            {operator.subCategory}
                                                                        </td>

                                                                        {/* Operator Code */}
                                                                        <td className="px-3 py-2 align-middle">
                                                                            {operator.operatorCode ? (
                                                                                <div className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                                                                                    <span>{operator.operatorCode}</span>
                                                                                    <button
                                                                                        onClick={() => copyText(operator.operatorCode, "Operator Code")}
                                                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                                        title="Copy Operator Code"
                                                                                    >
                                                                                        <FaRegCopy className="h-4 w-4" />
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                "-"
                                                                            )}
                                                                        </td>

                                                                        {/* Handler */}
                                                                        <td className="px-3 py-2 align-middle text-gray-700 dark:text-gray-200">
                                                                            {operator.handler || "-"}
                                                                        </td>

                                                                        {/* Status */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            <Switch
                                                                                checked={operator.status === "Active"}
                                                                                onChange={(enabled) => handleStatusChange(operator.id, enabled)}
                                                                                className={`${operator.status === "Active" ? "bg-green-500" : "bg-red-500"
                                                                                    } relative inline-flex h-5 w-9 items-center rounded-full`}
                                                                            >
                                                                                <span className="sr-only">Toggle status</span>
                                                                                <span
                                                                                    className={`${operator.status === "Active" ? "translate-x-5" : "translate-x-1"
                                                                                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                                                                />
                                                                            </Switch>
                                                                        </td>

                                                                        {/* Actions */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            <div className="flex justify-center gap-2">
                                                                                <button
                                                                                    onClick={() => openModal(operator)}
                                                                                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                                                                    title="View Operator"
                                                                                >
                                                                                    <EyeIcon className="h-5 w-5 text-blue-500" />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() =>
                                                                                        window.open(`/plan-slap?operatorId=${operator.id}`, "_blank")
                                                                                    }
                                                                                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                                                                    title="Margins"
                                                                                >
                                                                                    <ChartBarIcon className="h-5 w-5 text-green-600" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                                                        <p className="text-gray-500 dark:text-gray-400">No operators found</p>
                                                    </div>
                                                )}

                                                {/* Pagination */}
                                                <div className="flex items-center justify-between px-4 py-3 mt-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                                                    <div className="flex-1 flex justify-between sm:hidden">
                                                        <button
                                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                            disabled={pagination.currentPage === 1}
                                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                                                        >
                                                            Previous
                                                        </button>
                                                        <button
                                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                            disabled={pagination.currentPage === pagination.totalPages}
                                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                                        <div>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                                                                <span className="font-medium">
                                                                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                                                                </span>{' '}
                                                                of <span className="font-medium">{pagination.totalItems}</span> results
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                                <button
                                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                                    disabled={pagination.currentPage === 1}
                                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                                >
                                                                    <span className="sr-only">Previous</span>
                                                                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                                                </button>

                                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                                    let pageNum;
                                                                    if (pagination.totalPages <= 5) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.currentPage <= 3) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                                                        pageNum = pagination.totalPages - 4 + i;
                                                                    } else {
                                                                        pageNum = pagination.currentPage - 2 + i;
                                                                    }

                                                                    return (
                                                                        <button
                                                                            key={pageNum}
                                                                            onClick={() => handlePageChange(pageNum)}
                                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                                                                }`}
                                                                        >
                                                                            {pageNum}
                                                                        </button>
                                                                    );
                                                                })}

                                                                <button
                                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                                >
                                                                    <span className="sr-only">Next</span>
                                                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                                                </button>
                                                            </nav>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </main>
                </main>
            </div>
            <AnimatePresence>
                {isModalOpen && (
                    <>
                        {/* Blurred Background Overlay */}
                        <motion.div
                            className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                        />

                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div className="relative w-full max-w-4xl">
                                <motion.div
                                    className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
                                    initial={{ scale: 0.95, y: 20, opacity: 0 }}
                                    animate={{
                                        scale: 1,
                                        y: 0,
                                        opacity: 1,
                                        transition: {
                                            type: 'spring',
                                            stiffness: 300,
                                            damping: 25
                                        }
                                    }}
                                    exit={{ scale: 0.95, y: 20, opacity: 0 }}
                                >

                                    <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10">
                                        <h3 className="text-lg font-semibold dark:text-white">{isAddMode ? 'Add New Operator' : `Operator Details - ${selectedOperator?.name}`}</h3>
                                        <button
                                            onClick={closeModal}
                                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                                        >
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>

                                    {/* Content Area */}
                                    <div className="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
                                        {/* Logo Preview Section */}
                                        <div className="flex flex-col items-center mb-6">
                                            <div className="w-32 h-32 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
                                                {formData.logo ? (
                                                    <img
                                                        src={formData.logo}
                                                        alt="Operator Logo"
                                                        className="max-h-24 max-w-full object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                {!formData.logo && (
                                                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">
                                                        <span className="text-2xl font-bold">
                                                            {formData.name ? formData.name.charAt(0).toUpperCase() : 'O'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {(isEditing || isAddMode) && (
                                                <p className="text-sm text-gray-500 text-center">
                                                    Logo will appear here when URL is valid
                                                </p>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Operator Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Biller Name
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <input
                                                        type="text"
                                                        value={formData.name}
                                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.name}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Operator Code */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Operator Code
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <input
                                                        type="text"
                                                        value={formData.operatorCode}
                                                        onChange={(e) => handleFieldChange('operatorCode', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.operatorCode}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Provider ID */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Provider ID
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <input
                                                        type="text"
                                                        value={formData.providerId}
                                                        onChange={(e) => handleFieldChange('providerId', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.providerId}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Logo URL */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Logo URL
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <div className="flex space-x-2">
                                                        <input
                                                            type="text"
                                                            value={formData.logo}
                                                            onChange={(e) => handleFieldChange('logo', e.target.value)}
                                                            className="flex-1 rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="https://example.com/logo.png"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (formData.logo) {
                                                                    window.open(formData.logo, '_blank');
                                                                }
                                                            }}
                                                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                                            disabled={!formData.logo}
                                                        >
                                                            Preview
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-700 font-medium break-all">
                                                        {selectedOperator?.logo}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Category */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Category
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <select
                                                        value={formData.category}
                                                        onChange={(e) => handleFieldChange('category', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">Select Category</option>
                                                        {categories.map((category) => (
                                                            <option key={category} value={category}>
                                                                {category}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.category}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Sub Category */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Sub Category
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <input
                                                        type="text"
                                                        value={formData.subCategory}
                                                        onChange={(e) => handleFieldChange('subCategory', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.subCategory}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Is Credit */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Is Credit
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <select
                                                        value={formData.isCredit}
                                                        onChange={(e) => handleFieldChange('isCredit', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="1">Yes</option>
                                                        <option value="0">No</option>
                                                    </select>
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.isCredit === "1" ? "Yes" : "No"}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Handler */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Handler
                                                </label>
                                                {isEditing || isAddMode ? (
                                                    <input
                                                        type="text"
                                                        value={formData.handler}
                                                        onChange={(e) => handleFieldChange('handler', e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                ) : (
                                                    <p className="text-gray-700 font-medium">
                                                        {selectedOperator?.handler}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Status */}
                                            {!isAddMode && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Status
                                                    </label>
                                                    {isEditing ? (
                                                        <select
                                                            value={formData.status}
                                                            onChange={(e) => handleFieldChange('status', e.target.value)}
                                                            className="w-full rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        >
                                                            <option value="Active">Active</option>
                                                            <option value="Inactive">Inactive</option>
                                                        </select>
                                                    ) : (
                                                        <p className="text-gray-700 font-medium">
                                                            {selectedOperator?.status}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Fixed Footer */}
                                    <div className="flex justify-end space-x-3 p-4 border-t dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
                                        <div className="flex justify-end space-x-3">
                                            {isEditing || isAddMode ? (
                                                <>
                                                    <motion.button
                                                        type="button"
                                                        onClick={handleSave}
                                                        className="px-4 py-2 bg-white text-blue-600 font-bold rounded-md shadow hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white"
                                                        whileHover={{ scale: 1.03 }}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        {isAddMode ? 'Add Operator' : 'Save Changes'}
                                                    </motion.button>
                                                    <motion.button
                                                        type="button"
                                                        onClick={handleCancelEdit}
                                                        className="px-4 py-2 border border-white text-white font-medium rounded-md bg-red-700 shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-white"
                                                        whileHover={{ scale: 1.03 }}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        Cancel
                                                    </motion.button>
                                                </>
                                            ) : (
                                                <motion.button
                                                    type="button"
                                                    onClick={handleEditClick}
                                                    className="px-4 py-2 bg-white text-blue-600 font-bold rounded-md shadow hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white"
                                                    whileHover={{ scale: 1.03 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    Edit Operator
                                                </motion.button>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
            <ToastContainer />
        </div>
    );
};

export default Operators;